import React, { useRef, useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { PlaceholderBox } from "./primitives";
import { ArrowRight, Stethoscope } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const line = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.2 + i * 0.12, ease: [0.16, 1, 0.3, 1] } }),
};

const item = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

const Masked = ({ children, i }) => (
  <span className="block overflow-hidden">
    <motion.span className="block" variants={line} custom={i} initial="hidden" animate="show">
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const [photo, setPhoto] = useState("");

  useEffect(() => {
    axios.get(`${API}/doctor-photo`).then((res) => {
      setPhoto(res.data.image_base64 || "");
    }).catch(() => {});
  }, []);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen bg-[#14263D] text-white overflow-hidden pt-28 pb-16"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(60% 50% at 78% 40%, rgba(111,211,222,0.10), transparent 70%)" }}
      />

      <div className="relative max-w-[1240px] mx-auto px-5 md:px-8 grid lg:grid-cols-[1.05fr_0.95fr] gap-8 items-center min-h-[calc(100vh-11rem)]">
        <motion.div
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.12, delayChildren: 0.85 } } }}
          initial="hidden"
          animate="show"
        >
          <motion.div variants={item}>
            <span className="eyebrow text-[#5ECCA0] inline-flex items-center gap-2">
              <Stethoscope size={14} /> MCh Neurosurgery · Bhubaneswar, Odisha
            </span>
          </motion.div>

          <h1 className="font-display font-light mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight">
            <Masked i={0}>Precision care for</Masked>
            <Masked i={1}>
              the <span className="italic text-[#5ECCA0]">brain and spine.</span>
            </Masked>
          </h1>

          <motion.p
            variants={item}
            className="mt-7 text-base md:text-lg text-[#F3F5F4]/75 max-w-xl leading-relaxed"
          >
            Fifteen years of practice built on clear explanations, honest options and
            modern, minimally invasive technique — so you understand your condition
            before any decision is made.
          </motion.p>

          <motion.p
            variants={item}
            className="font-mono-ui text-[0.7rem] md:text-xs text-[#5ECCA0] mt-6 tracking-wide uppercase"
          >
            MBBS · MS · MCh Neurosurgery · Fellowship, Minimally Invasive Spine Surgery
          </motion.p>

          <motion.div variants={item} className="mt-9 flex flex-wrap gap-3">
            <a
              href="#contact"
              data-testid="hero-book-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-[#5ECCA0] text-[#14263D] font-medium px-6 py-3.5 hover-lift hover:shadow-lg hover:shadow-[#5ECCA0]/25"
            >
              Book a Consultation
              <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#symptoms"
              data-testid="hero-symptoms-btn"
              className="inline-flex items-center rounded-full border border-white/25 text-white px-6 py-3.5 hover:border-[#5ECCA0] hover:text-[#5ECCA0] transition-colors"
            >
              Understand Your Symptoms
            </a>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.6, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="hidden lg:flex items-center justify-center"
        >
          <div className="relative z-10 w-full max-w-[260px]">
            {photo ? (
              <div className="aspect-[4/5] rounded-2xl overflow-hidden border border-white/10" data-testid="hero-doctor-photo">
                <img src={photo} alt="Dr. Amit Chanduka" className="w-full h-full object-cover" />
              </div>
            ) : (
              <PlaceholderBox
                onDark
                ratio="aspect-[4/5]"
                label="Dr. Chanduka's photo — professional headshot, non-clinical, portrait orientation"
              />
            )}
          </div>
        </motion.div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 eyebrow text-[#5ECCA0] flex flex-col items-center gap-2">
        <span>Scroll</span>
        <span className="w-px h-8 bg-gradient-to-b from-[#5ECCA0] to-transparent" />
      </div>
    </section>
  );
};

export default Hero;
